<template>
  <div>
    <organism-repository
      v-if="filterData"
      :filter-list="filterData"
      :currentVeeamId="currentServerVeeam.id"
      :fields="fields"
      :items="repositoryList"
      :serverList="serverList"
      :mountServer="mountServer"
      :currentRepository="currentRepository"
      @onUpdateFilterData="onUpdateFilterData"
      @onUpdateCurrentPage="onUpdateCurrentPage"
      @onSetDefaultFilterData="onSetDefaultFilterData"
      @selectRow="getRepositoryByIdFromApi"
      @createRepository="createRepositoryByApi"
      @updateRepository="updateRepositoryByApi"
      @deleteRepository="deleteRepositoryByApi"
    ></organism-repository>
  </div>
</template>

<script>
import mixins from "@/mixins"
import mixinsValidator from "@/mixins/mixinsValidator"
import {
  getVeeamServer,
  getBackupList,
  getRepositoryStatusList,
  getRepositoryById,
  createRepository,
  updateRepository,
  deleteRepository,
} from "@/api/api-module3"
import OrganismRepository from "@/components/organisms/backupManagement/OrganismRepository.vue"
import { mapActions, mapGetters } from "vuex"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"

export default {
  name: "Repository",
  components: { OrganismRepository },
  mixins: [mixins, mixinsValidator, mixinsObjectHelper],
  data() {
    return {
      fields: [
        {
          key: "repository",
          label: "NAME",
          thClass: "w-220px",
          tdClass: "w-220px",
        },
        {
          key: "type",
          label: "TYPE",
          thClass: "w-120px",
          tdClass: "w-120px",
        },
        {
          key: "path",
          label: "PATH",
        },
        {
          key: "capacity",
          label: "CAPACITY",
          thClass: "w-150px",
          tdClass: "w-150px",
        },
        {
          key: "freeSpace",
          label: "FREE SPACE",
          thClass: "w-150px",
          tdClass: "w-150px",
        },
        {
          key: "deleteIcon",
          label: "",
          thClass: "w-150px",
          tdClass: "w-150px",
        },
      ],
      repositoryList: [],
      serverList: [],
      mountServer: {
        mountServerId: null,
        writeCacheFolder: null,
        vPowerNFSEnabled: true,
        vPowerNFSPortSettings: {
          mountPort: 0,
          vPowerNFSPort: 0,
        },
      },
      backupRepositoryList: [],
      currentRepository: null,
      filterData: null,
    }
  },
  computed: {
    ...mapGetters("app", ["getCurrentServerVeeam"]),
    ...mapGetters("appFilter", ["getBMMRepositoryFilter"]),

    currentServerVeeam() {
      return this.getCurrentServerVeeam
    },
  },
  watch: {
    currentServerVeeam() {
      if (this.hasString(this.currentServerVeeam.id)) {
        this.getDataFromApi()
      }
    },
    currentPage() {
      this.getDataFromApi()
    },
  },
  async mounted() {
    await this.setShowVeeam(true)
    await this.getDataFromApi()
  },
  beforeDestroy() {
    this.setShowVeeam(false)
  },
  methods: {
    ...mapActions("app", ["setShowVeeam", "setCurrentStatusVeeam"]),
    ...mapActions("appFilter", ["setBMMRepositoryFilter"]),

    cloneFilterDataFromVuex() {
      const filterDataFromVuex = this.getBMMRepositoryFilter
      this.filterData = this.deepCopyObject(filterDataFromVuex)
    },

    async getServerList() {
      this.showLoading(true)
      this.serverList = []
      const filter = {
        veeamId: this.currentServerVeeam.id,
      }
      await getVeeamServer(filter)
        .then((response) => {
          if (response) {
            response.data.forEach((server) => {
              if (server.type !== "ViHost")
                this.serverList.push({
                  id: server.id,
                  server: server.name,
                  type:
                    server.type === "WindowsHost" ? "WinLocal" : "LinuxLocal",
                })

              if (this.isInvalidIpV4(server.name))
                this.mountServer = {
                  mountServerId: server.id,
                  writeCacheFolder: "C:\\ProgramData\\Veeam\\Backup\\IRCache\\",
                  vPowerNFSEnabled: true,
                  vPowerNFSPortSettings: {
                    mountPort: 1058,
                    vPowerNFSPort: 2049,
                  },
                }
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getDataFromApi() {
      this.cloneFilterDataFromVuex()
      if (this.currentServerVeeam.id) {
        await Promise.all([
          this.getRepositoryListFromApi(),
          this.getServerList(),
        ])
      }
    },

    async getRepositoryListFromApi() {
      this.showLoading(true)
      this.cloneFilterDataFromVuex()
      const filterData = this.filterDecoration()
      await this.getBackupRepositoryListFromApi()
      await getRepositoryStatusList(filterData)
        .then((response) => {
          if (response) {
            this.filterData.totalCount = response.pagination?.total
            const updatedFilter = {
              ...this.getBMMRepositoryFilter,
              totalCount: response.pagination?.total,
            }
            this.setBMMRepositoryFilter(updatedFilter)
            this.repositoryList = response.data.map((repo) => {
              return {
                id: repo.id,
                description: repo.description,
                repository: {
                  name: repo.name,
                  ip: repo.hostName,
                },
                type: repo.type,
                path: repo.path,
                capacity: `${repo.capacityGb} GB`,
                freeSpace: `${repo.freeGb} GB`,
                usedSpace: `${repo.usedSpaceGb} GB`,
                isDelete: !this.backupRepositoryList.includes(repo.id),
              }
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    filterDecoration() {
      const filter = {
        Page: this.filterData.currentPage,
        nameFilter: this.filterData.name
          ? `*${this.filterData.name}*`
          : undefined,
        typeFilter: this.filterData.type || undefined,
        veeamId: this.currentServerVeeam?.id,
      }
      return filter
    },

    // click filter
    onUpdateFilterData(filter) {
      filter.currentPage = 1
      this.setBMMRepositoryFilter(filter)
      this.getRepositoryListFromApi()
    },

    onUpdateCurrentPage(filter) {
      const updatedFilter = {
        ...this.getBMMRepositoryFilter,
        currentPage: filter.currentPage,
      }
      this.setBMMRepositoryFilter(updatedFilter)
      this.getRepositoryListFromApi()
    },

    onSetDefaultFilterData() {
      const defaultFilter = {
        name: "",
        type: "",
        currentPage: 1,
        totalCount: 0,
      }
      this.setBMMRepositoryFilter(defaultFilter)
      this.getRepositoryListFromApi()
    },

    async getRepositoryByIdFromApi(id) {
      this.showLoading(true)
      this.currentRepository = null
      await getRepositoryById(id, this.currentServerVeeam.id)
        .then((response) => {
          if (response) {
            this.currentRepository = {
              id: response.id,
              name: response.name,
              server: response.hostname,
              description: response.description,
              tag: response.tag,
              type: response.type,
              hostId: response.hostId,
              path: response.repository.path,
              limitTask: response.repository.maxTaskCount,
              readWriteRate: response.repository.readWriteRate,
              advancedSettings: response.repository.advancedSettings,
              mountServer: response.mountServer,
            }
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getBackupRepositoryListFromApi() {
      this.showLoading(true)
      this.backupRepositoryList = []
      const baseFilter = { veeamId: this.currentServerVeeam.id }
      await getBackupList(baseFilter)
        .then((response) => {
          if (response) {
            response.data.data.forEach((backupRepository) => {
              this.backupRepositoryList.push(backupRepository.repositoryId)
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    async createRepositoryByApi(request) {
      this.showLoading(true)
      const duplicateName = this.repositoryList.find((repository) => {
        return repository.repository.name === request.name
      })
      if (duplicateName) {
        this.toastError(`${duplicateName.repository.name} is already use`)
      } else {
        await createRepository(this.currentServerVeeam.id, request)
          .then((response) => {
            if (response && response.status === "ok") {
              setTimeout(() => {
                this.toastSuccess("Repository created successfully")
                this.getDataFromApi()
              }, 12000)
            }
          })
          .catch(() => {
            this.toastError("Repository created failed")
          })
          .finally(() => this.showLoading(false))
      }
    },

    async updateRepositoryByApi(id, request) {
      this.showLoading(true)
      await updateRepository(id, this.currentServerVeeam.id, request)
        .then(async (response) => {
          if (response && response.status === "ok") {
            setTimeout(() => {
              this.toastSuccess("Repository updated successfully")
              this.getDataFromApi()
            }, 12000)
          }
        })
        .catch(() => {
          this.toastError("Repository updated failed")
        })
        .finally(() => this.showLoading(false))
    },

    async deleteRepositoryByApi(id) {
      this.showLoading(true)
      await deleteRepository(id, this.currentServerVeeam.id)
        .then(async (response) => {
          if (response && response.status === "ok") {
            setTimeout(() => {
              this.toastSuccess("Repository deleted successfully")
              this.getDataFromApi()
            }, 12000)
          }
        })
        .catch(() => {
          this.toastError("Repository deleted failed")
        })
        .finally(() => this.showLoading(false))
    },
  },
}
</script>

<style lang="scss" scoped></style>
